import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  ConfigProvider,
  Dropdown,
  InputNumber,
  Pagination,
  Table,
  Tabs,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import {
  Add,
  EditIcon,
  InactiveIcon,
  BackArrowIcon,
  EyeIcon,
} from "../../../../icons";
import "./style.scss";
import ConfirmModal from "../../../../components/ModalLayout/components/ConfirmModal";
import AddRole from "../../../../components/DrawerLayout/components/AddRole";
import { useRoles, useUpdateRole } from "../../../../hooks/useRoles";
import FilterDropdownRoles from "../../../../components/FilterDropdownRoles";
import { isPermit } from "../../../../utils/permissionCheck";
import PermissionDenied from "../../../OtherPage/PermissionDenied";

const pageSize = 10;

const StatusPill: React.FC<{ status: number }> = ({ status }) => {
  const label = status === 1 ? "Active" : status === 0 ? "Inactive" : "Deleted";
  return (
    <span className={`status-pill status-${label.toLowerCase()}`}>{label}</span>
  );
};

const ManageRole: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "active" | "inactive">(
    "all"
  );
  const [drawerMode, setDrawerMode] = useState<"add" | "edit" | "view">("add");
  const [selectedRole, setSelectedRole] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState<any>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});

  const navigate = useNavigate();

  // Fetch roles using API hook
  const {
    data: rolesData,
    isLoading,
    isError,
  } = useRoles({
    page: currentPage,
    perPage: pageSize,
    status_in:
      activeTab === "all" ? undefined : activeTab === "active" ? [1] : [0],
    paginate: true,
    ...filters,
  });

  const [allTotal, setAllTotal] = useState<number>(0);

  const tableData = isError ? [] : (rolesData as any)?.roles || [];
  const totalRoles = isError ? 0 : (rolesData as any)?.meta?.total || 0;
  const tableHeight = window.innerHeight - 400;
  const updateRoleMutation = useUpdateRole();
  const total = (rolesData as any)?.meta?.total ?? 0;

  const [allTotalPagination, setAllTotalPagination] = useState(total);

  // Track total only once for "All" tab
  useEffect(() => {
    if (activeTab === "all" && currentPage === 1) {
      setAllTotal(total);
      setAllTotalPagination((rolesData as any)?.meta?.total);
    } else if (currentPage === 1) {
      setAllTotalPagination((rolesData as any)?.meta?.total);
    }
  }, [rolesData, activeTab]);

  // --- Delete Role ---
  const handleDelete = () => {
    if (!modalProps?.record?.role_id) {
      message.warning("Invalid role selected.");
      return;
    }

    updateRoleMutation.mutate(
      { action: "delete", role_ids: [Number(modalProps.record?.role_id)] },
      {
        onSuccess: () => {
          message.success("Role deleted successfully!");
          setModalOpen(false);
          setModalProps(null);
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.message || "Failed to merge tickets";
          message.error(errorMessage);
        },
      }
    );
  };

  const handleRemoveRoleFromDrawer = () => {
    if (selectedRole) {
      setModalProps({
        record: selectedRole,
        title: "Confirm Delete",
        message: `Are you sure you want to delete ${selectedRole.role_name}?`,
        onConfirm: handleDelete,
        confirmText: "Delete",
      });
      setModalOpen(true);
      setDrawerOpen(false);
    }
  };

  // --- Dropdown Actions ---
  const ActionMenu = (record: Member) => {
    const items = [
      { icon: <EyeIcon />, key: "view", label: "View" },
      ...(isPermit(5, "edit")
        ? [{ icon: <EditIcon />, key: "edit", label: "Edit" }]
        : []),
      ...(isPermit(5, "delete")
        ? [
            {
              icon: <InactiveIcon />,
              key: "delete",
              danger: true,
              label: "Remove",
            },
          ]
        : []),
    ];

    return {
      items,
      onClick: ({ key }: { key: string }) => {
        if (key === "view") {
          setDrawerMode("view");
          setDrawerOpen(true);
          setSelectedRole(record);
        }
        if (key === "edit") {
          setDrawerMode("edit");
          setDrawerOpen(true);
          setSelectedRole(record);
        }
        if (key === "delete") {
          setModalProps({
            record,
            title: "Confirm Delete",
            message: `Are you sure you want to delete ${record.role_name}?`,
            onConfirm: handleDelete,
            confirmText: "Delete",
          });
          setModalOpen(true);
        }
      },
    };
  };

  // --- Table Columns ---
  const columns: ColumnsType<any> = [
    {
      title: "Role",
      dataIndex: "role_name",
      key: "role_name",
      width: 300,
      fixed: "left",
      render: (text: string, record: any) => (
        <span
          className="cursor-pointer"
          onClick={() => {
            setDrawerMode("view");
            setDrawerOpen(true);
            setSelectedRole(record);
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Users",
      dataIndex: "no_of_users",
      key: "no_of_users",
      width: 200,
      render: (val: number) => `${val} users`,
    },
    {
      title: "Status",
      dataIndex: "role_status_flg",
      key: "role_status_flg",
      width: 200,
      render: (status: number) => <StatusPill status={status} />,
    },
    {
      title: "Created on",
      dataIndex: "created_date",
      key: "created_date",
      width: 250,
    },
    {
      fixed: "right",
      title: "",
      key: "actions",
      width: 80,
      render: (_text, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown menu={ActionMenu(record)} trigger={["click"]}>
            <Button icon={<MoreOutlined />} type="text" />
          </Dropdown>
        </div>
      ),
    },
  ];

  // --- Filters for dropdown ---
  const tableHeaders = columns
    .filter((col: any) => {
      if (!col.key || col.key === "actions" || col.key === "no_of_users")
        return false;
      // ❌ Hide "Status" filter when not on "all" tab
      if (activeTab !== "all" && col.dataIndex === "role_status_flg")
        return false;
      return true;
    })
    .map((col: any) => ({ label: col.title, value: col.dataIndex }));

  const optionsByColumn: Record<string, string[]> = {
    role_name: [
      ...new Set(tableData.map((r: { role_name: any }) => r.role_name)),
    ],
    role_status_flg: [
      ...new Set(
        tableData.map((r: { role_status_flg: any }) => r.role_status_flg)
      ),
    ],
    created_date: [
      ...new Set(tableData.map((r: { created_date: any }) => r.created_date)),
    ],
  };

  // --- Render ---
  return (
    <div className="dashboard">
      {isPermit(5, "view") ? (
        <>
          {" "}
          <div className="role-header">
            <div className="flex items-center gap-4">
              <BackArrowIcon
                className="cursor-pointer"
                onClick={() => navigate("/user")}
                width={24}
                height={24}
              />
              <h2 className="role-title">Manage Role</h2>

              <ConfigProvider
                theme={{
                  components: {
                    Tabs: {
                      inkBarColor: "#000",
                      itemSelectedColor: "#000",
                      itemHoverColor: "#333",
                      itemColor: "#000",
                    },
                  },
                }}
              >
                <Tabs
                  activeKey={activeTab}
                  onChange={(key) => {
                    setCurrentPage(1);

                    setActiveTab(key as "all" | "active" | "inactive");
                  }}
                  items={[
                    { key: "all", label: `All (${allTotal})` },
                    { key: "active", label: "Active" },
                    { key: "inactive", label: "Inactive" },
                  ]}
                  className="status-tabs"
                />
              </ConfigProvider>
            </div>

            {isPermit(5, "create") && (
              <button
                className="add-role-btn"
                onClick={() => {
                  setDrawerMode("add");
                  setSelectedRole(null);
                  setDrawerOpen(true);
                }}
              >
                <span className="mr-1">
                  <Add />
                </span>
                ADD ROLE
              </button>
            )}
          </div>
          <div className="table-controls flex justify-end items-center my-4">
            <div className="table-filter-wrapper flex gap-3">
              <FilterDropdownRoles
                activeTab={activeTab}
                tableHeaders={tableHeaders}
                optionsByColumn={optionsByColumn}
                currentPage={currentPage}
                perPage={pageSize}
                onChange={({ apiParams }) => setFilters(apiParams)}
              />
            </div>
          </div>
          <Table
            rowKey="role_id"
            columns={columns}
            dataSource={tableData}
            scroll={{ x: 1000, y: tableHeight }}
            pagination={false}
            loading={isLoading}
          />
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Page
              <InputNumber
                controls={false}
                min={1}
                max={
                  allTotalPagination
                    ? Math.ceil(allTotalPagination / pageSize)
                    : 1
                }
                value={currentPage}
                onChange={(val) => setCurrentPage(val || 1)}
                style={{ width: 60 }}
              />
              <span>
                of{" "}
                {allTotalPagination
                  ? Math.ceil(allTotalPagination / pageSize)
                  : 1}
              </span>
            </div>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={allTotalPagination}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
            />
          </div>
          <AddRole
            open={drawerOpen}
            setOpen={setDrawerOpen}
            title={
              drawerMode === "view"
                ? "Role Details"
                : drawerMode === "edit"
                ? "Edit Role"
                : "Add New Role"
            }
            user={selectedRole}
            mode={drawerMode}
            onEditClick={() => setDrawerMode("edit")}
            onRemoveRole={handleRemoveRoleFromDrawer}
          />
          {modalOpen && (
            <ConfirmModal
              open={modalOpen}
              loading={updateRoleMutation.isPending}
              title={modalProps?.title ?? ""}
              message={modalProps?.message}
              confirmText={modalProps?.confirmText}
              onClose={() => {
                setModalOpen(false);
                setModalProps(null);
              }}
              onConfirm={handleDelete}
            />
          )}
        </>
      ) : (
        <PermissionDenied />
      )}
    </div>
  );
};

export default ManageRole;
